import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { CardView, rem } from 'design-system';
import { WebView } from 'utils/packages/webview';
import { isWeb } from '../../../constants';
import DisputeIframe from './DisputeIframe';
const INITIAL_WEBVIEW_HEIGHT = rem(400);
// Reveal is normally driven by the `ready` signal the page posts once the
// dispute form is visible. This is only a hard fallback so we don't show the
// spinner forever if that signal never arrives (unexpected TU layout).
const READY_FALLBACK_MS = 2000;
// After verification we ask the parent to drop its loader first, then wait
// this long before flipping the WebView wrapper to visible. Without the gap
// React commits the local "visible" flip before the parent commits the
// loader removal, so both appear in the same frame and the layout jumps.
const REVEAL_DELAY_MS = 100;
// TU returns 200 + a "soft" error page when the dispute service is degraded.
// We scan the rendered body for these strings inside the WebView and post
// `{ type: 'error' }` back so the host can swap to its own error UI.
const TU_ERROR_STRING_MATCHES = ['Service is unavailable. Please try again'];
// TU's dispute page is built for an ~800px desktop iframe and has no
// React-Native-friendly resize protocol. Inject (a) a viewport meta + CSS shim
// to reflow the form to the WebView's actual width, (b) a postMessage
// reporter that pings the host with document height on load and on every event
// the TU integration guide flags as content-changing (blur/change/click/onchange),
// plus a MutationObserver as a safety net, and (c) an error-text scanner that
// fires once if TU's soft-error copy appears in the page. The host sets the
// WebView's height from height messages and unmounts the WebView on error
// messages. WebView's injected script is not subject to the browser
// same-origin policy, so this runs against TU's page.
const INJECTED_SCRIPT = `
  (function() {
    if (!window.__emmaDisputeSetup) {
      window.__emmaDisputeSetup = true;

      if (!document.querySelector('meta[name="viewport"]')) {
        var meta = document.createElement('meta');
        meta.setAttribute('name', 'viewport');
        meta.setAttribute('content', 'width=device-width, initial-scale=1');
        document.head.appendChild(meta);
      }

      // NB: don't force \`min-height: 0\` on html/body — TU's loading page uses
      // \`min-height: 100vh\` to vertically-center its spinner, and collapsing it
      // clips the spinner.
      var css =
        'html, body { overflow-x: hidden !important; margin: 0 !important; width: 100% !important; height: auto !important;}' +
        'body { padding: 32px 16px !important; box-sizing: border-box !important; }' +
        '* { max-width: 100% !important; box-sizing: border-box !important; }' +
        'input[type="text"], textarea, select { width: 100% !important; }' +
        'table { width: 100% !important; table-layout: fixed !important; }';
      var style = document.createElement('style');
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);

      var ERROR_STRINGS = ${JSON.stringify(TU_ERROR_STRING_MATCHES)};

      function measureContentHeight() {
        if (!document.body) return 0;
        return document.body.offsetHeight;
      }

      function checkForError() {
        if (window.__emmaErrorReported) return;
        if (!document.body) return;
        var text = document.body.innerText || '';
        for (var i = 0; i < ERROR_STRINGS.length; i++) {
          if (text.indexOf(ERROR_STRINGS[i]) !== -1) {
            window.__emmaErrorReported = true;
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: 'error', match: ERROR_STRINGS[i] })
              );
            }
            return;
          }
        }
      }

      // The loading page has no visible form controls; the dispute form does.
      // Treat the first visible field as the signal that the form (not the
      // spinner) is on screen.
      function isFormReady() {
        var el = document.querySelector(
          'input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=reset]), select, textarea'
        );
        return !!el && el.offsetParent !== null && el.offsetHeight > 0;
      }

      function checkReady() {
        if (window.__emmaReadyReported || window.__emmaErrorReported) return;
        if (!isFormReady()) return;
        window.__emmaReadyReported = true;
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'ready' })
          );
        }
      }

      var timer = null;
      window.__emmaPostHeight = function() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function() {
          checkForError();
          checkReady();
          var h = measureContentHeight();
          if (window.ReactNativeWebView && h > 0) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: 'height', value: h })
            );
          }
        }, 100);
      };

      ['load', 'blur', 'change', 'click', 'input', 'resize'].forEach(function(ev) {
        document.addEventListener(ev, window.__emmaPostHeight, true);
      });

      if (typeof MutationObserver !== 'undefined' && document.body) {
        new MutationObserver(window.__emmaPostHeight).observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    }

    if (window.__emmaPostHeight) window.__emmaPostHeight();
  })();
  true;
`;
const NativeDispute = ({ html, onError, onReady }) => {
    const webViewRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(INITIAL_WEBVIEW_HEIGHT);
    const [isReady, setIsReady] = useState(false);
    const readyTimer = useRef(null);
    const revealTimer = useRef(null);
    const clearTimers = useCallback(() => {
        if (readyTimer.current) {
            clearTimeout(readyTimer.current);
            readyTimer.current = null;
        }
        if (revealTimer.current) {
            clearTimeout(revealTimer.current);
            revealTimer.current = null;
        }
    }, []);
    useEffect(() => clearTimers, [clearTimers]);
    // New html → new page load → re-gate visibility.
    useEffect(() => {
        setIsReady(false);
        clearTimers();
    }, [html, clearTimers]);
    // Drop the parent's loader, then flip the WebView visible a tick later so the
    // loader unmounts first (without the gap both commit in the same frame and
    // the layout jumps).
    const reveal = useCallback(() => {
        clearTimers();
        onReady();
        revealTimer.current = setTimeout(() => setIsReady(true), REVEAL_DELAY_MS);
    }, [clearTimers, onReady]);
    const onMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.nativeEvent.data);
            if (data?.type === 'error') {
                clearTimers();
                onError();
                return;
            }
            // The page tells us the dispute form is actually on screen
            if (data?.type === 'ready') {
                reveal();
                return;
            }
            if (data?.type === 'height' &&
                typeof data.value === 'number' &&
                data.value > 0) {
                setContentHeight(data.value);
            }
        }
        catch {
            // Ignore non-JSON messages.
        }
    }, [clearTimers, onError, reveal]);
    const onLoadEnd = useCallback(() => {
        webViewRef.current?.injectJavaScript(INJECTED_SCRIPT);
        // Reveal is driven by the page's `ready` signal. Reset a hard fallback on
        // every load (the form auto-submits, so there are 2+ loads) in case that
        // signal never arrives.
        clearTimers();
        readyTimer.current = setTimeout(reveal, READY_FALLBACK_MS);
    }, [clearTimers, reveal]);
    return (<View style={isReady ? styles.nativeContainer : styles.nativeContainerHidden}>
      <CardView noMarginTop style={styles.webViewContainer}>
        <WebView ref={webViewRef} source={{ html }} originWhitelist={['*']} style={[styles.webview, { height: contentHeight }]} javaScriptEnabled domStorageEnabled scrollEnabled={false} nestedScrollEnabled injectedJavaScript={INJECTED_SCRIPT} onLoadEnd={onLoadEnd} onMessage={onMessage}/>
      </CardView>
    </View>);
};
const DisputeWebView = ({ formFields, html, onError, onReady }) => {
    if (isWeb) {
        return <DisputeIframe formFields={formFields}/>;
    }
    return (<NativeDispute html={html ?? ''} onError={onError} onReady={onReady}/>);
};
export default DisputeWebView;
const styles = StyleSheet.create({
    webViewContainer: {
        minHeight: rem(440),
        marginHorizontal: rem(16),
        flex: 1,
    },
    webview: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    nativeContainer: {
        flex: 1,
    },
    // Keep the WebView mounted and rendering (so the injected error scanner
    // can run) but visually absent — parent shows the screen-level loader.
    nativeContainerHidden: {
        position: 'absolute',
        top: -10000,
        left: 0,
        right: 0,
        height: rem(440),
        opacity: 0,
    },
});
